// import components
import Button from '@sdb/ui/atoms/Button';
import { Box, FormControlLabel, Switch, Tooltip } from '@mui/material';
import {
  StyledBoxFlexJustifyEnd,
  StyledBoxFlexJustifyEndGap
} from 'components/shared/Box/Box.styles';
import MenuButton from 'components/shared/MenuButton';
import { DateNavigation } from 'components/shared';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import SwitcherComponent from 'components/admin-portal/BaseTabsRouting/components/Headers/SwitcherComponent';
import {
  DateNavigationContainer,
  PlanUnitSelectorContainer,
  StyledHeader,
  StyledUnsavedDataAlertBox,
  Top
} from './Header.styles';

// import external libraries
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

// import types
import { ClaimsAction } from 'components/roster-portal/home/travel-expense-claims/types';
import { HeaderProps } from './Header.types';
import UnsavedDataAlert from 'components/shared/CRUD/components/InnerPage/components/UnsavedDataAlert';
import { SingleOrRange } from 'components/shared/DateNavigation/DateNavigation.types';

const Header = (props: HeaderProps) => {
  const {
    onAction,
    onSelectedDate,
    onSelectPlanUnit,
    selectedDate,
    selection = [],
    showActions,
    showPlanUnitSelector,
    isTravelMovements = false,
    onlyApprovableTravelMovements = true,
    handleToggleChange,
    approveToggleDisabled = false,
    period,
    showSwitcherComponent = false,
    switcherComponentData,
    showSingleAction = false,
    onSingleAction,
    singleActionButtonLabel,
    showCustomMenuItemLabel,
    menuOptions,
    unsavedDataAlert,
    unsavedDataAlertContent,
    allowDateNavigationMenuOptions = false,
    dateNavigationOptions,
    selectedDateRange,
    onSelectedDateRange
  } = props;
  const { t } = useTranslation('common');

  const handleSelectedDateChange = (date: SingleOrRange | null) => {
    if (Array.isArray(date)) {
      onSelectedDateRange?.(date);
      onSelectedDate(date[0] ? dayjs(date[0]).toDate() : null);
      return;
    }

    onSelectedDate(date ? dayjs(date).toDate() : null);
  };

  const defaultOptions = [
    {
      label: t(`travelExpenseClaims.actions.${ClaimsAction.APPROVE}`),
      onClick: () => onAction?.(ClaimsAction.APPROVE)
    },
    {
      label: t(`travelExpenseClaims.actions.${ClaimsAction.REJECT}`),
      onClick: () => onAction?.(ClaimsAction.REJECT)
    }
  ];

  return (
    <StyledHeader as="header">
      <Top>
        <Box>
          {showPlanUnitSelector && onSelectPlanUnit && (
            <PlanUnitSelectorContainer>
              <PlanUnitSelector onSelected={onSelectPlanUnit} />
              {isTravelMovements && (
                <Tooltip
                  title={
                    approveToggleDisabled
                      ? t('travelMovements.toBeApprovedFixedTooltip')
                      : ''
                  }
                  arrow
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={onlyApprovableTravelMovements}
                        disabled={approveToggleDisabled}
                      />
                    }
                    label={`${t('travelMovements.toBeApproved')}`}
                    onChange={handleToggleChange}
                  />
                </Tooltip>
              )}
            </PlanUnitSelectorContainer>
          )}

          {showSwitcherComponent && switcherComponentData && (
            <SwitcherComponent
              tabbedElement={switcherComponentData?.tabbedElement}
              tabulatedItems={switcherComponentData?.tabulatedItems}
              setTabbedElement={switcherComponentData?.setTabbedElement}
              disableRouting={switcherComponentData?.disableRouting}
              showCustomMenuItemLabel={showCustomMenuItemLabel}
            />
          )}
        </Box>
        <DateNavigationContainer>
          <DateNavigation
            defaultPeriod={period ?? 'month'}
            // A caller-driven period must not be overwritten by the app-wide
            // saved preference: DateNavigation re-runs its mount effect on every
            // `defaultPeriod` change, which would restore the preference over it.
            persistPeriodPreference={period === undefined}
            defaultDate={selectedDate}
            defaultDateRange={selectedDateRange}
            onDateChange={handleSelectedDateChange}
            options={dateNavigationOptions}
            allowMenuOptions={allowDateNavigationMenuOptions}
          />
        </DateNavigationContainer>
        <StyledBoxFlexJustifyEndGap>
          {unsavedDataAlert && (
            <StyledUnsavedDataAlertBox>
              <UnsavedDataAlert textContent={unsavedDataAlertContent} />
            </StyledUnsavedDataAlertBox>
          )}

          {showSingleAction && onSingleAction && singleActionButtonLabel && (
            <StyledBoxFlexJustifyEnd>
              <Button variant="contained" onClick={onSingleAction}>
                {singleActionButtonLabel}
              </Button>
            </StyledBoxFlexJustifyEnd>
          )}

          {showActions && (
            <StyledBoxFlexJustifyEnd>
              <MenuButton
                label={t('actions')}
                disabled={selection.length === 0}
                options={menuOptions ?? defaultOptions}
              />
            </StyledBoxFlexJustifyEnd>
          )}
        </StyledBoxFlexJustifyEndGap>
      </Top>
    </StyledHeader>
  );
};

export default Header;
